import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import confetti from 'canvas-confetti';
import axios from 'axios';
import { Sun, Moon, Sunrise, Sunset, PartyPopper, Palmtree, Coffee, Sparkles, Award, TrendingUp, Target, Calendar, Rocket, Brain, CheckCircle2, Zap, Trophy, Star } from 'lucide-react';

// ============================================
// RANDOM GREETING MESSAGES SYSTEM
// ============================================

// Helper to get random item from array (avoiding repeat of last)
// Identität einer Nachricht: Titel allein reicht nicht - z.B. teilen sich alle
// morningMessages den Titel "Guten Morgen" und unterscheiden sich nur im Untertitel.
const messageKey = (m) => `${m?.title || ''}|${m?.subtitle || ''}`;

const getRandomMessage = (messages, lastMessageKey) => {
    if (!messages || messages.length === 0) return null;

    const lastUsed = localStorage.getItem(lastMessageKey);
    let availableMessages = messages;

    // Filter out last used message if we have more than one.
    // Nie auf eine leere Liste filtern - sonst ist die Auswahl undefined.
    if (lastUsed && messages.length > 1) {
        const filtered = messages.filter(m => messageKey(m) !== lastUsed);
        if (filtered.length > 0) availableMessages = filtered;
    }

    const selected = availableMessages[Math.floor(Math.random() * availableMessages.length)] || messages[0];
    try {
        localStorage.setItem(lastMessageKey, messageKey(selected));
    } catch { /* Storage voll oder blockiert - nicht kritisch */ }
    return selected;
};

// MORNING MESSAGES (05:00 - 11:00)
const morningMessages = [
    { emoji: '🌅', title: 'Guten Morgen', subtitle: 'Bereit für einen produktiven Tag?' },
    { emoji: '☕', title: 'Guten Morgen', subtitle: 'Erst Kaffee, dann Welt erobern.' },
    { emoji: '🌞', title: 'Guten Morgen', subtitle: 'Mal sehen, was wir heute alles schaffen.' },
    { emoji: '🚀', title: 'Guten Morgen', subtitle: 'Zeit, ein paar Dinge von der Liste zu streichen.' },
    { emoji: '✨', title: 'Guten Morgen', subtitle: 'Heute wird ein guter Tag.' },
    { emoji: '🧠', title: 'Guten Morgen', subtitle: 'Deine Tasks warten schon auf dich.' },
    { emoji: '🌞', title: 'Guten Morgen', subtitle: 'Frischer Tag, frische Ideen.' },
];

// DAY MESSAGES (11:00 - 17:00)
const dayMessages = [
    { emoji: '👋', title: 'Willkommen zurück', subtitle: 'Weiter geht\'s.' },
    { emoji: '☀️', title: 'Schön dich zu sehen', subtitle: 'Weiter geht\'s.' },
    { emoji: '🚀', title: 'Alles bereit', subtitle: 'Lass uns was bewegen.' },
    { emoji: '📋', title: 'Willkommen zurück', subtitle: 'Ein paar Tasks warten noch.' },
    { emoji: '⚡', title: 'Willkommen zurück', subtitle: 'Zeit für den nächsten Fortschritt.' },
    { emoji: '🎯', title: 'Zurück im System', subtitle: 'Was steht als nächstes an?' },
];

// EVENING MESSAGES (17:00+)
const eveningMessages = [
    { emoji: '🌙', title: 'Guten Abend', subtitle: 'Noch ein paar letzte Dinge?' },
    { emoji: '🌆', title: 'Willkommen zurück', subtitle: 'Der Endspurt läuft.' },
    { emoji: '✨', title: 'Guten Abend', subtitle: 'Vielleicht noch ein letzter Task?' },
    { emoji: '🌙', title: 'Noch kurz reinschauen?', subtitle: 'Wir sind bereit.' },
    { emoji: '🧠', title: 'Abendmodus aktiviert', subtitle: 'Was steht noch an?' },
];

// MONDAY MESSAGES
const mondayMessages = [
    { emoji: '🚀', title: 'Neue Woche, neue Chancen', subtitle: 'Los geht\'s!' },
    { emoji: '☕', title: 'Montag', subtitle: 'Lass uns loslegen.' },
    { emoji: '📅', title: 'Frische Woche', subtitle: 'Was steht als erstes an?' },
    { emoji: '⚡', title: 'Montag gestartet', subtitle: 'Zeit für Fortschritt.' },
    { emoji: '🌟', title: 'Willkommen in der neuen Woche', subtitle: 'Bereit für neue Herausforderungen?' },
];

// TASKS COMPLETED MESSAGES
const tasksCompletedMessages = [
    { emoji: '🎉', title: 'Nice!', subtitle: 'Das war der letzte Task für heute.' },
    { emoji: '🏁', title: 'Geschafft!', subtitle: 'Heute ist alles erledigt.' },
    { emoji: '✨', title: 'Stark!', subtitle: 'Deine Liste ist leer.' },
    { emoji: '📋', title: 'Alles erledigt', subtitle: 'Saubere Arbeit.' },
    { emoji: '🚀', title: 'Alle Tasks erledigt', subtitle: 'Guter Tag!' },
];

// VACATION TOMORROW MESSAGES
const vacationTomorrowMessages = [
    { emoji: '🌴', title: 'Morgen geht\'s in den Urlaub!', subtitle: 'Heute noch entspannt abschließen.' },
    { emoji: '✈️', title: 'Fast geschafft', subtitle: 'Morgen beginnt deine Auszeit.' },
    { emoji: '🌞', title: 'Noch heute – dann Urlaub', subtitle: 'Du hast es dir verdient!' },
    { emoji: '🏖️', title: 'Morgen startet dein Urlaub', subtitle: 'Genieß den Tag.' },
];

// VACATION RETURN MESSAGES
const vacationReturnMessages = [
    { emoji: '🎉', title: 'Willkommen zurück', subtitle: 'Energie getankt?' },
    { emoji: '🌟', title: 'Schön dich wieder hier zu sehen', subtitle: 'Hoffentlich gut erholt!' },
    { emoji: '🚀', title: 'Zurück aus dem Urlaub!', subtitle: 'Bereit für neue Projekte?' },
    { emoji: '🌞', title: 'Hoffentlich gut erholt', subtitle: 'Los geht\'s.' },
    { emoji: '✨', title: 'Willkommen zurück im Team', subtitle: 'Wir haben dich vermisst!' },
];

// AFTER PAUSE / INACTIVITY MESSAGES
const afterPauseMessages = [
    { emoji: '👋', title: 'Willkommen zurück', subtitle: 'Schön, dass du wieder da bist.' },
    { emoji: '⚡', title: 'Weiter geht\'s', subtitle: 'Wo waren wir stehen geblieben?' },
    { emoji: '📋', title: 'Zeit für den nächsten Schritt', subtitle: 'Deine Tasks warten.' },
    { emoji: '🚀', title: 'Zurück im Flow', subtitle: 'Lass uns loslegen!' },
];

// LEVEL UP / ACHIEVEMENT MESSAGES
const levelUpMessages = [
    { emoji: '🎉', title: 'Neues Achievement freigeschaltet!', subtitle: 'Weiter so!' },
    { emoji: '🏆', title: 'Stark!', subtitle: 'Ein neuer Meilenstein.' },
    { emoji: '🚀', title: 'Level Up!', subtitle: 'Du wächst über dich hinaus.' },
    { emoji: '✨', title: 'Weiter so!', subtitle: 'Du bist auf dem richtigen Weg.' },
];

// BIRTHDAY MESSAGES
const birthdayMessages = [
    { emoji: '🎂', title: 'Alles Gute zum Geburtstag', subtitle: 'Feier schön!' },
    { emoji: '🎉', title: 'Heute wird gefeiert!', subtitle: 'Happy Birthday!' },
    { emoji: '🎂', title: 'Happy Birthday!', subtitle: 'Genieß den Tag.' },
];

// LOGOUT / END OF DAY MESSAGES
const logoutMessages = [
    { emoji: '🌇', title: 'Feierabend', subtitle: 'Bis morgen!' },
    { emoji: '✨', title: 'Gute Arbeit heute', subtitle: 'Erhol dich gut.' },
    { emoji: '👋', title: 'Bis morgen', subtitle: 'Mach\'s gut!' },
    { emoji: '🌙', title: 'Zeit runterzufahren', subtitle: 'Schönen Abend noch!' },
];

// Dynamic motivation quotes for month start
const monthQuotes = [
    '"Der beste Weg, die Zukunft vorherzusagen, ist sie zu gestalten." - Peter Drucker',
    '"Erfolg ist die Summe kleiner Anstrengungen, die Tag für Tag wiederholt werden." - Robert Collier',
    '"Motivation bringt dich in Gang. Gewohnheit bringt dich voran." - Jim Ryun',
    '"Jeder Experte war einmal ein Anfänger." - Helen Hayes',
    '"Der einzige Weg, großartige Arbeit zu leisten, ist zu lieben, was man tut." - Steve Jobs',
    '"Qualität bedeutet, es richtig zu machen, auch wenn keiner zuschaut." - Henry Ford',
    '"Wer aufhört, besser werden zu wollen, hört auf, gut zu sein." - Marie von Ebner-Eschenbach',
    '"Erfolg besteht darin, von Misserfolg zu Misserfolg zu gehen, ohne seine Begeisterung zu verlieren." - Winston Churchill',
    '"Die Zukunft gehört denen, die an die Schönheit ihrer Träume glauben." - Eleanor Roosevelt',
    '"Es ist nie zu spät, das zu werden, was man hätte sein können." - George Eliot',
    '"Der Anfang ist die Hälfte des Ganzen." - Aristoteles',
    '"Träume nicht dein Leben, lebe deinen Traum." - Mark Twain'
];

const getRandomQuote = () => {
    return monthQuotes[Math.floor(Math.random() * monthQuotes.length)];
};

// Icon mapping for emojis
const iconMap = {
    '🌅': Sunrise,
    '☕': Coffee,
    '🌞': Sun,
    '🚀': Rocket,
    '✨': Sparkles,
    '🧠': Brain,
    '👋': Sun,
    '☀️': Sun,
    '📋': Target,
    '⚡': Zap,
    '🎯': Target,
    '🌙': Moon,
    '🌆': Sunset,
    '📅': Calendar,
    '🌟': Star,
    '🎉': PartyPopper,
    '🏁': CheckCircle2,
    '🌴': Palmtree,
    '✈️': Palmtree,
    '🏖️': Palmtree,
    '🏆': Trophy,
    '🎂': PartyPopper,
    '🌇': Sunset,
};

// Get the right messages based on greeting type
const getMessagesForType = (type) => {
    switch (type) {
        case 'morning':
            return morningMessages;
        case 'day':
            return dayMessages;
        case 'evening':
            return eveningMessages;
        case 'monday':
            return mondayMessages;
        case 'tasks_completed':
            return tasksCompletedMessages;
        case 'vacation_tomorrow':
            return vacationTomorrowMessages;
        case 'vacation_return':
            return vacationReturnMessages;
        case 'after_pause':
            return afterPauseMessages;
        case 'level_up':
            return levelUpMessages;
        case 'birthday':
            return birthdayMessages;
        case 'logout':
        case 'logout_vacation':
            return logoutMessages;
        default:
            return dayMessages;
    }
};

// Color gradients for different times
const getGradientForType = (type) => {
    switch (type) {
        case 'morning':
            return 'from-orange-400/20 via-yellow-300/10 to-transparent';
        case 'day':
            return 'from-yellow-400/20 via-amber-200/10 to-transparent';
        case 'evening':
            return 'from-indigo-500/20 via-purple-400/10 to-transparent';
        case 'monday':
            return 'from-amber-400/20 via-orange-300/10 to-transparent';
        case 'month_start':
            return 'from-purple-400/20 via-pink-300/10 to-transparent';
        case 'birthday':
            return 'from-pink-400/20 via-purple-300/10 to-transparent';
        case 'vacation_tomorrow':
        case 'vacation_return':
            return 'from-emerald-400/20 via-teal-300/10 to-transparent';
        case 'logout':
        case 'logout_vacation':
            return 'from-orange-500/20 via-red-400/10 to-transparent';
        case 'level_up':
            return 'from-yellow-400/20 via-amber-300/10 to-transparent';
        case 'year_review':
            return 'from-amber-400/20 via-yellow-300/10 to-transparent';
        default:
            return 'from-blue-400/20 via-cyan-300/10 to-transparent';
    }
};

// Icon color for different types
const getIconColorForType = (type) => {
    switch (type) {
        case 'morning':
            return 'text-orange-400';
        case 'day':
            return 'text-yellow-500';
        case 'evening':
            return 'text-indigo-400';
        case 'monday':
            return 'text-amber-500';
        case 'month_start':
            return 'text-purple-400';
        case 'birthday':
            return 'text-pink-500';
        case 'vacation_tomorrow':
        case 'vacation_return':
            return 'text-emerald-500';
        case 'logout':
        case 'logout_vacation':
            return 'text-orange-500';
        case 'level_up':
            return 'text-yellow-500';
        case 'year_review':
            return 'text-amber-500';
        default:
            return 'text-blue-500';
    }
};

// Should show confetti for this type?
const shouldShowConfetti = (type) => {
    return ['monday', 'birthday', 'vacation_return', 'year_review', 'level_up', 'tasks_completed'].includes(type);
};

// Confetti config for different types
const getConfettiConfig = (type) => {
    const defaultConfig = {
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#009dde', '#34c759', '#ffcc00', '#ff3b30']
    };
    
    switch (type) {
        case 'birthday':
            return {
                particleCount: 150,
                spread: 100,
                origin: { y: 0.5 },
                colors: ['#ff69b4', '#ff1493', '#da70d6', '#ba55d3', '#9370db']
            };
        case 'monday':
            return {
                particleCount: 80,
                spread: 100,
                origin: { y: 0.4 },
                colors: ['#f59e0b', '#fbbf24', '#fcd34d', '#78350f']
            };
        case 'year_review':
            return {
                particleCount: 200,
                spread: 120,
                origin: { y: 0.3 },
                colors: ['#fbbf24', '#f59e0b', '#d97706', '#92400e', '#78350f']
            };
        case 'level_up':
            return {
                particleCount: 100,
                spread: 80,
                origin: { y: 0.5 },
                colors: ['#fbbf24', '#f59e0b', '#34c759', '#22c55e']
            };
        default:
            return defaultConfig;
    }
};

// Year Review Component
const YearReviewOverlay = ({ stats, userName, onClose }) => {
    const statItems = [
        { icon: TrendingUp, label: 'Stunden gearbeitet', value: stats.total_hours, suffix: 'h', color: 'text-blue-500' },
        { icon: Target, label: 'Tasks erledigt', value: stats.completed_tasks, color: 'text-emerald-500' },
        { icon: Award, label: 'Meilensteine erreicht', value: stats.completed_milestones, color: 'text-purple-500' },
        { icon: Calendar, label: 'Urlaubstage genommen', value: stats.vacation_days_used, color: 'text-orange-500' },
        { icon: Sparkles, label: 'Challenge-Punkte', value: stats.points_earned, color: 'text-yellow-500' },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 backdrop-blur-xl cursor-pointer overflow-y-auto py-8"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: 'spring', bounce: 0.3 }}
                className="text-center px-6 py-8 max-w-md w-full mx-4"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mb-8"
                >
                    <div className="inline-flex p-4 rounded-full bg-gradient-to-br from-amber-400/30 to-orange-500/20 mb-4">
                        <Award className="w-10 h-10 text-amber-500" />
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        🎊 Dein Jahr {stats.year}
                    </h1>
                    <p className="text-muted-foreground">Großartig gemacht, {userName}!</p>
                </motion.div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-8">
                    {statItems.map((stat, index) => (
                        <motion.div
                            key={stat.label}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                            className={`p-4 rounded-xl bg-card border border-border/50 shadow-soft ${index === 4 ? 'col-span-2' : ''}`}
                        >
                            <stat.icon className={`w-6 h-6 ${stat.color} mx-auto mb-2`} />
                            <div className="text-2xl font-bold">
                                {stat.value.toLocaleString('de-DE')}{stat.suffix || ''}
                            </div>
                            <div className="text-xs text-muted-foreground">{stat.label}</div>
                        </motion.div>
                    ))}
                </div>

                {/* Closing message */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                    className="space-y-4"
                >
                    <p className="text-lg font-medium">
                        🚀 Auf ein noch besseres {stats.year + 1}!
                    </p>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                        Weiter zum Dashboard
                    </button>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

const GreetingOverlay = () => {
    const { user, showGreeting, greetingType, dismissGreeting, token } = useAuth();
    const [yearStats, setYearStats] = useState(null);
    const [showYearReview, setShowYearReview] = useState(false);
    
    // Get random message for this greeting type
    const randomMessage = useMemo(() => {
        if (!greetingType) return null;
        
        // Special case for month_start and year_review
        if (greetingType === 'month_start') {
            return {
                emoji: '✨',
                title: 'Willkommen im neuen Monat',
                subtitle: getRandomQuote()
            };
        }
        
        if (greetingType === 'year_review') {
            return {
                emoji: '🎊',
                title: 'Jahresrückblick',
                subtitle: 'Was für ein Jahr! Schau dir deine Erfolge an.'
            };
        }
        
        const messages = getMessagesForType(greetingType);
        return getRandomMessage(messages, `lastGreeting_${greetingType}`);
    }, [greetingType]);
    
    // Fetch year review stats when needed
    useEffect(() => {
        const fetchYearStats = async () => {
            if (greetingType === 'year_review' && token) {
                try {
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    const response = await axios.get(`${API_URL}/api/stats/year-review`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setYearStats(response.data);
                    setShowYearReview(true);
                } catch (error) {
                    console.error('Failed to fetch year stats:', error);
                }
            }
        };
        
        if (showGreeting && greetingType === 'year_review') {
            fetchYearStats();
        }
    }, [showGreeting, greetingType, token]);
    
    useEffect(() => {
        if (showGreeting && shouldShowConfetti(greetingType)) {
            const config = getConfettiConfig(greetingType);
            confetti(config);
            
            // Extra confetti burst for special occasions
            if (greetingType === 'birthday' || greetingType === 'year_review') {
                setTimeout(() => confetti({ ...config, origin: { x: 0.2, y: 0.5 } }), 300);
                setTimeout(() => confetti({ ...config, origin: { x: 0.8, y: 0.5 } }), 600);
            }
        }
    }, [showGreeting, greetingType]);

    const userName = user?.first_name || 'User';
    const gradient = getGradientForType(greetingType);
    const iconColor = getIconColorForType(greetingType);
    
    // Get the icon component based on emoji
    const Icon = randomMessage?.emoji ? (iconMap[randomMessage.emoji] || Sun) : Sun;

    const handleClose = () => {
        setShowYearReview(false);
        setYearStats(null);
        dismissGreeting();
    };

    // Show Year Review overlay if applicable
    if (showYearReview && yearStats) {
        return <YearReviewOverlay stats={yearStats} userName={userName} onClose={handleClose} />;
    }

    if (!randomMessage) return null;

    return (
        <AnimatePresence>
            {showGreeting && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-xl cursor-pointer"
                    onClick={handleClose}
                    data-testid="greeting-overlay"
                >
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: -20 }}
                        transition={{ duration: 0.5, type: 'spring', bounce: 0.3 }}
                        className="text-center px-8 py-12 max-w-lg"
                    >
                        {/* Gradient background */}
                        <div className={`absolute inset-0 bg-gradient-radial ${gradient} opacity-50 blur-3xl`} />
                        
                        {/* Icon */}
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring', bounce: 0.5 }}
                            className="relative mb-6"
                        >
                            <div className={`inline-flex p-6 rounded-full bg-card shadow-floating ${iconColor}`}>
                                <Icon className="w-12 h-12" />
                            </div>
                        </motion.div>

                        {/* Text */}
                        <motion.h1
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                            className="relative text-3xl md:text-4xl font-semibold tracking-tight mb-3"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                            {randomMessage.emoji} {randomMessage.title}{greetingType !== 'logout' && greetingType !== 'logout_vacation' ? `, ${userName}!` : '!'}
                        </motion.h1>
                        
                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="relative text-lg text-muted-foreground max-w-md mx-auto"
                        >
                            {randomMessage.subtitle}
                        </motion.p>

                        {/* Skip hint */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.5 }}
                            transition={{ delay: 1 }}
                            className="relative mt-8 text-sm text-muted-foreground"
                        >
                            Klicken zum Überspringen
                        </motion.p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default GreetingOverlay;
